/**
 * Photo overlay geometry.
 *
 * Every overlay is authored in a fixed design space per aspect ratio. Layer
 * coordinates (x, y, width, height, font size) are absolute design pixels and
 * the design size is stored in photo_overlays.reference_width and
 * reference_height. At bake time the whole design canvas is scaled by one
 * uniform factor to the photo and centered, so nothing is ever stretched.
 *
 * Overlays saved by the previous editor stored preview-screen pixels against
 * whatever canvas size the saving device had; toDesignSpace converts those on
 * load using the stored reference size.
 *
 * Pure module: no DOM, no Fabric, so it is unit tested directly.
 */

export const DESIGN_SIZES = {
  '1:1': { width: 1600, height: 1600 },
  '2:1': { width: 2000, height: 1000 },
  '3:2': { width: 1800, height: 1200 },
  '4:3': { width: 1600, height: 1200 },
  '9:16': { width: 1080, height: 1920 },
  '16:9': { width: 1920, height: 1080 },
  '20:9': { width: 2000, height: 900 },
}

export const DEFAULT_ASPECT = '16:9'

/**
 * Design-space size for an aspect ratio label. Unknown or missing labels fall
 * back to 16:9. Returns a fresh object so callers can never mutate the table.
 * @param {string} aspectRatio e.g. '16:9'
 * @returns {{width: number, height: number}}
 */
export function designSizeFor(aspectRatio) {
  const size = DESIGN_SIZES[aspectRatio] || DESIGN_SIZES[DEFAULT_ASPECT]
  return { width: size.width, height: size.height }
}

/**
 * Parse an aspect label such as '16:9' to a number (16 / 9).
 * @param {string} label
 * @returns {number|null} null when malformed
 */
export function parseAspect(label) {
  if (typeof label !== 'string') return null
  const parts = label.split(':')
  if (parts.length !== 2) return null
  const w = parseFloat(parts[0])
  const h = parseFloat(parts[1])
  if (!isFinite(w) || !isFinite(h) || w <= 0 || h <= 0) return null
  return w / h
}

const SCALED_X_KEYS = ['x', 'width']
const SCALED_Y_KEYS = ['y', 'height']
const FONT_KEYS = ['fontSize', 'font_size']

/**
 * Convert layers stored against (referenceWidth, referenceHeight) into the
 * given design space. Returns new layer objects; the input is not mutated.
 * Scales x and width by design.width / referenceWidth, y and height by
 * design.height / referenceHeight, and the font size (whichever of fontSize
 * or font_size the layer carries) by the horizontal factor. When either
 * reference value is missing or zero the layers are copied unchanged.
 * Idempotent once the reference equals the design size.
 * @param {Array<Object>} layers
 * @param {number} referenceWidth
 * @param {number} referenceHeight
 * @param {{width: number, height: number}} design
 * @returns {Array<Object>}
 */
export function toDesignSpace(layers, referenceWidth, referenceHeight, design) {
  const list = Array.isArray(layers) ? layers : []
  const rw = Number(referenceWidth)
  const rh = Number(referenceHeight)
  if (!rw || !rh || !design || !design.width || !design.height) {
    return list.map(layer => ({ ...layer }))
  }
  const sx = design.width / rw
  const sy = design.height / rh
  return list.map(layer => {
    const out = { ...layer }
    for (const key of SCALED_X_KEYS) {
      if (out[key] != null && out[key] !== '') out[key] = Number(out[key]) * sx
    }
    for (const key of SCALED_Y_KEYS) {
      if (out[key] != null && out[key] !== '') out[key] = Number(out[key]) * sy
    }
    for (const key of FONT_KEYS) {
      if (out[key] != null && out[key] !== '') out[key] = Number(out[key]) * sx
    }
    return out
  })
}

/**
 * Uniform contain transform of a design canvas into a photo: one scale factor
 * (the smaller of the two axis ratios) and the offsets that center the scaled
 * canvas on the photo. For a photo whose aspect matches the design this is
 * identical to scaling each axis independently.
 * @param {{width: number, height: number}} design
 * @param {number} photoWidth
 * @param {number} photoHeight
 * @returns {{scale: number, offsetX: number, offsetY: number}}
 */
export function fitTransform(design, photoWidth, photoHeight) {
  const scale = Math.min(photoWidth / design.width, photoHeight / design.height)
  return {
    scale,
    offsetX: (photoWidth - design.width * scale) / 2,
    offsetY: (photoHeight - design.height * scale) / 2,
  }
}

/**
 * The largest centered area of a photo with the design's aspect ratio, in
 * whole photo pixels. Used when an overlay crops the photo to its shape.
 * @param {{width: number, height: number}} design
 * @param {number} photoWidth
 * @param {number} photoHeight
 * @returns {{sx: number, sy: number, sw: number, sh: number}}
 */
export function coverCropRect(design, photoWidth, photoHeight) {
  const target = design.width / design.height
  let sw = photoWidth
  let sh = photoHeight
  if (photoWidth / photoHeight > target) sw = Math.min(photoWidth, Math.round(photoHeight * target))
  else sh = Math.min(photoHeight, Math.round(photoWidth / target))
  return {
    sx: Math.floor((photoWidth - sw) / 2),
    sy: Math.floor((photoHeight - sh) / 2),
    sw,
    sh,
  }
}

/**
 * Where the design artboard sits on a photo-sized area, in that area's
 * pixels: the same placement as the bake (fitTransform). The live camera
 * draws a crop overlay here and dims everything outside it.
 * @param {{width: number, height: number}} design
 * @param {number} areaWidth
 * @param {number} areaHeight
 * @returns {{x: number, y: number, width: number, height: number}}
 */
export function artboardRect(design, areaWidth, areaHeight) {
  const fit = fitTransform(design, areaWidth, areaHeight)
  return {
    x: fit.offsetX,
    y: fit.offsetY,
    width: design.width * fit.scale,
    height: design.height * fit.scale,
  }
}

/**
 * Layout of the adjust drawer's stage for a photo, in design units.
 * The artboard is always fit inside the photo exactly as the bake does, so
 * `scale` (photo pixels to design units) is the same either way:
 * - crop off: the stage is the whole photo and the artboard sits centered in
 *   it at `artOffset`; the backdrop starts at 0,0.
 * - crop on: the stage is the artboard itself and the backdrop is shifted by
 *   a negative offset so only the photo's centered area (coverCropRect) shows.
 * @param {{width: number, height: number}} design
 * @param {number} imageWidth photo pixels
 * @param {number} imageHeight photo pixels
 * @param {boolean} crop
 * @returns {{scale: number, frame: {width: number, height: number}, artOffset: {x: number, y: number}, backdrop: {left: number, top: number, width: number, height: number}}}
 */
export function stageLayout(design, imageWidth, imageHeight, crop) {
  const s = Math.min(imageWidth / design.width, imageHeight / design.height)
  const scale = 1 / s
  const photo = { width: imageWidth * scale, height: imageHeight * scale }
  if (crop) {
    return {
      scale,
      frame: { width: design.width, height: design.height },
      artOffset: { x: 0, y: 0 },
      backdrop: { left: -(photo.width - design.width) / 2, top: -(photo.height - design.height) / 2, width: photo.width, height: photo.height },
    }
  }
  return {
    scale,
    frame: { width: photo.width, height: photo.height },
    artOffset: { x: (photo.width - design.width) / 2, y: (photo.height - design.height) / 2 },
    backdrop: { left: 0, top: 0, width: photo.width, height: photo.height },
  }
}

/**
 * True when the overlay crops the photo to its aspect ratio
 * (photo_overlays.crop_to_overlay, 0 or 1 from the API).
 * @param {Object|null} overlay
 * @returns {boolean}
 */
export function cropsToOverlay(overlay) {
  if (!overlay) return false
  return overlay.crop_to_overlay === true || Number(overlay.crop_to_overlay) === 1
}

/**
 * True when two aspect ratios (labels or numbers) differ by more than 2
 * percent. Malformed input counts as a mismatch.
 * @param {string|number} aspectA
 * @param {string|number} aspectB
 * @returns {boolean}
 */
export function aspectMismatch(aspectA, aspectB) {
  const a = typeof aspectA === 'number' ? aspectA : parseAspect(aspectA)
  const b = typeof aspectB === 'number' ? aspectB : parseAspect(aspectB)
  if (!a || !b) return true
  return Math.abs(a - b) / b > 0.02
}

/**
 * Which layers a photo bakes with: its stored adjustment when it has one for
 * this overlay, otherwise the overlay template. The override is the JSON
 * string stored in player_photos.overlay_layers:
 *   { reference_width, reference_height, layers: [...] }
 * Returns { layers, referenceWidth, referenceHeight, source } where source is
 * 'override' or 'template'. Layers are returned as stored; callers pass the
 * reference sizes on to toDesignSpace or bakeOverlay.
 */
export function effectiveLayers(overlay, overrideJson) {
  const template = {
    layers: parseLayers(overlay && overlay.layers),
    referenceWidth: Number(overlay && overlay.reference_width) || 0,
    referenceHeight: Number(overlay && overlay.reference_height) || 0,
    source: 'template',
  }
  if (!overrideJson) return template
  try {
    const parsed = typeof overrideJson === 'string' ? JSON.parse(overrideJson) : overrideJson
    if (!parsed || !Array.isArray(parsed.layers) || !Number(parsed.reference_width) || !Number(parsed.reference_height)) return template
    return {
      layers: parsed.layers.map(l => ({ ...l })),
      referenceWidth: Number(parsed.reference_width),
      referenceHeight: Number(parsed.reference_height),
      source: 'override',
    }
  } catch (e) {
    return template
  }
}

function parseLayers(value) {
  if (Array.isArray(value)) return value.map(l => ({ ...l }))
  if (typeof value !== 'string' || value === '') return []
  try {
    const arr = JSON.parse(value)
    return Array.isArray(arr) ? arr : []
  } catch (e) {
    return []
  }
}
